import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  addMinutes,
  clockLabel,
  fetchFreeBusy,
  fitLabel,
  minutesOf,
  type FreeBusySuggestion,
} from '../api/freeBusy'
import AvailabilityGrid, { type AvailabilityDay } from './AvailabilityGrid'

/**
 * Categorical, never the accent.
 *
 * The accent has to keep meaning "this is the one you picked" once several
 * lanes are on screen, so the lanes borrow the chart colours instead.
 */
const TEACHER_COLOR = 'var(--color-cat-2)'
const DANCER_COLOR = 'var(--color-cat-1)'

type Props = {
  dancerIds: string[]
  teacherStaffId: string
  /** How long the lesson is, so a gap too short is never offered. */
  minutes: number
  picked?: { date: string; start: string } | null
  onPick: (date: string, start: string, end: string) => void
  /** What to call the teacher in the legend. Never comes from the feed. */
  teacherLabel?: string
}

/**
 * Suggested times, and everybody's week beneath them.
 *
 * **Both halves, deliberately.** The list is the fluid path and answers most
 * asks in one tap; the grid is how somebody picks a time the list did not
 * offer, which is the whole reason it is drawn rather than described.
 */
export default function SchedulingAssistant({
  dancerIds,
  teacherStaffId,
  minutes,
  picked,
  onPick,
  teacherLabel = 'Teacher',
}: Props) {
  // Off by default: most privates are weekday evenings, so anything else is a
  // deliberate choice rather than a list she has to read past.
  const [weekends, setWeekends] = useState(false)
  /*
   * **Off, like the other two** (D-429). A gap adjacent to a teacher's last
   * class scores as an evening, so 9:15pm was leading the list above 4:00pm the
   * next day — past the hour a dancer is normally taken home. It is a switch
   * rather than a deletion because a duet whose only shared hour is late has to
   * have somewhere to go, and because the family, not the app, knows whether
   * nine o'clock is workable.
   */
  const [late, setLate] = useState(false)
  const [daytime, setDaytime] = useState(false)

  const query = useQuery({
    queryKey: ['free-busy', teacherStaffId, dancerIds.join(','), minutes],
    queryFn: () => fetchFreeBusy(teacherStaffId, dancerIds, minutes),
    enabled: Boolean(teacherStaffId) && dancerIds.length > 0,
    // The app retries once by default, which is right for a flaky read and
    // wrong here: a 404 is a permission answer and cannot succeed on a second
    // attempt, so retrying only makes her wait for the same no.
    retry: (_count, error) => error.message !== 'not-available',
  })

  if (!teacherStaffId) return null

  if (query.isPending) {
    return <p className="text-[12.5px] text-ink-3">Looking for times…</p>
  }

  /*
   * **A refusal is not an empty week.** The route 404s a teacher this family
   * has no business with, and rendering that as "no times available" would be
   * a statement about the schedule rather than about permission — the shape
   * D-209 exists to stop.
   */
  if (query.isError) {
    /*
     * **A refusal and an outage are different sentences.** Only a 404 means
     * "not yours to see"; a 500 or the ~75 seconds after a deploy when every
     * database-backed endpoint refuses means "ask again shortly". Saying the
     * first about the second is a claim about permission made from a transient
     * failure, which is the same shape D-209 exists to stop.
     */
    const refused = query.error.message === 'not-available'

    return (
      <p className="rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-[12.5px] text-ink-2">
        {refused
          ? 'Times for this teacher are not available here. You can still pick a time below and ask.'
          : 'Could not load times just now. You can still pick a time below and ask.'}
      </p>
    )
  }

  const data = query.data

  /*
   * **The grid asks the same question the list does.** `Suggest` already skips
   * a block shorter than the lesson, so without this the two halves of one
   * screen disagreed: the list filtered by length and the grid beside it did
   * not. Tapping a 30-minute gap while asking for 105 minutes wrote an end 75
   * minutes past the gap, straight through whatever the teacher does next —
   * and nothing refused it, because the ask only validates end-after-start.
   *
   * It also removes the slivers. A 15-minute gap between back-to-back classes
   * drew an 8px target on a phone, which is not tappable and was never
   * bookable either.
   */
  /*
   * **The grid draws what is on offer, and nothing else** (D-428).
   *
   * It ran the full studio day with the whole school day washed in "free" —
   * promising availability the same screen was saying it could not vouch for.
   * With school hours off, the window starts where after-school does; with
   * weekends off, those rows are not there. Turning either on widens it back,
   * which is what makes the switch mean something visible.
   */
  /*
   * **Asking for weekends is asking about whole days, so the axis opens too**
   * (D-428). A Saturday has no school hours to be after, and `ScheduleGaps`
   * classifies every weekend block `AllDay` at its own start — so with weekends
   * on and school hours off, a chip read "Sat 5 Sep · 8:00am" above a row the
   * clip had narrowed to "free from 4:00pm". The list and the grid answering
   * differently is exactly what D-420 says must not happen.
   */
  const drawnStart = daytime || weekends ? data.windowStart : data.eveningFrom
  const isWeekend = (date: string) => {
    const [y, m, d] = date.split('-').map(Number)
    const day = new Date(y, m - 1, d).getDay()

    return day === 0 || day === 6
  }

  const days: AvailabilityDay[] = data.days
    .filter((day) => weekends || !isWeekend(day.date))
    .map((day) => ({
      date: day.date,
      free: day.free
        // A block that ends before the drawn window opens has nothing to draw.
        .filter((span) => minutesOf(span.end) > minutesOf(drawnStart))
        /*
         * **Clip the block itself, not only the axis it is drawn on** (D-428).
         *
         * Narrowing the window moved where a block was *painted* and left its
         * own start alone — so a free stretch running 8:00am to 9:00pm filled
         * the row from the 4pm tick, was read out as "free from 8:00am", and
         * booked <b>8:00am</b> when tapped. That is the exact lesson-at-eight-
         * in-the-morning this decision exists to prevent, arriving through the
         * door the same decision made the primary one.
         *
         * The drawn edge, the accessible name and the time it books have to be
         * one fact, so the clip happens here, before any of the three read it.
         */
        .map((span) =>
          minutesOf(span.start) < minutesOf(drawnStart) ? { ...span, start: drawnStart } : span,
        )
        // Length is judged on what is left after clipping, not before it.
        .filter((span) => minutesOf(span.end) - minutesOf(span.start) >= minutes),
      /*
       * **What she said wins over what we worked out** (D-674).
       *
       * `out` is derived from what happened to her classes — cancelled, or
       * covered — and it is a good guess. `unavailable` is a day she has told
       * the studio she is not in, and it arrives already worded by the studio
       * (A-16) rather than assembled here. When both are true they say nearly
       * the same thing, so the explicit one is the one to print.
       *
       * **The empty day needs it most.** Without a note, a day with no free
       * blocks is a blank row, which reads as *she is teaching all of it* —
       * the opposite of the truth, and the reading a parent acts on (D-209).
       */
      note: day.unavailable ?? (day.out ? `${teacherLabel} is away this day` : undefined),
      lanes: [
        { id: 'teacher', label: teacherLabel, color: TEACHER_COLOR, busy: day.teacher },
        { id: 'dancers', label: 'Dancers', color: DANCER_COLOR, busy: day.dancers },
      ],
    }))

  /*
   * **Weekdays after school, and the rest is opt-in** (D-426).
   *
   * `asked`: *"should prioritize times during the week... after school hours"*
   * and *"by default not show times during the school days, but maybe have a
   * way to show it"*. Most privates are weekday evenings, so those are the
   * list; a weekend or a school-day daytime is a deliberate choice, made with
   * a control that says what it is doing.
   *
   * **Hidden here rather than filtered in the feed.** The server still ranks
   * and returns everything, so nothing has to round-trip when she changes her
   * mind, and the "rank, never filter" rule stays true of the data.
   *
   * **This sentence was nearly made false** (D-429). The first version of the
   * late cap dropped those suggestions server-side, which would have left this
   * comment and D-426 both describing something the code no longer did — and
   * would have let the empty state below claim nothing fit above a gap that
   * fits. Anything that keeps a kind of time off this list belongs in the
   * filter below, as a fit with a switch, not in the feed.
   */
  const shown = data.suggestions.filter(
    (s) =>
      s.fit === 'Evening'
      || (s.fit === 'AllDay' && weekends)
      || (s.fit === 'Daytime' && daytime)
      || (s.fit === 'Late' && late),
  )

  const nothing = shown.length === 0
  const hiddenWeekend = data.suggestions.some((s) => s.fit === 'AllDay')
  const hiddenDaytime = data.suggestions.some((s) => s.fit === 'Daytime')
  const hiddenLate = data.suggestions.some((s) => s.fit === 'Late')

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-[11px] font-bold tracking-[0.14em] text-ink-2 uppercase">
            Times that suit you both
          </p>

          <div className="flex flex-wrap items-center gap-1.5">
            {/*
              Only when there is something to include. A duet or trio commonly
              has nothing but Evening suggestions, and a bare "Include" followed
              by no switches reads as a control that failed to load.
            */}
            {(hiddenWeekend || hiddenDaytime || hiddenLate) && (
              <span className="text-[11px] font-semibold text-ink-3">Include</span>
            )}
            {(hiddenWeekend || weekends) && (
              <Opt on={weekends} onClick={() => setWeekends((v) => !v)}>
                Weekends
              </Opt>
            )}
            {(hiddenDaytime || daytime) && (
              <Opt on={daytime} onClick={() => setDaytime((v) => !v)}>
                School hours
              </Opt>
            )}
            {(hiddenLate || late) && (
              <Opt on={late} onClick={() => setLate((v) => !v)}>
                After 9pm
              </Opt>
            )}
          </div>
        </div>

        {nothing ? (
          /*
           * A short list is the normal case for a duet or trio, so it has to
           * read as an answer rather than as a screen that failed to load.
           */
          <p className="mt-1.5 text-[12.5px] text-ink-2">
            {hiddenWeekend || hiddenDaytime || hiddenLate
              ? 'Nothing after school in the next fortnight. There is more behind the switches above — or try a shorter lesson, or pick a time below and ask anyway.'
              : `Nothing in the next fortnight fits ${minutes} minutes for everybody. Try a shorter lesson, or pick a time below and ask anyway.`}
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {shown.map((s) => (
              <SuggestionButton
                key={`${s.date}-${s.start}`}
                suggestion={s}
                on={picked?.date === s.date && picked.start === s.start}
                onClick={() => onPick(s.date, s.start, s.end)}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-[11px] font-bold tracking-[0.14em] text-ink-2 uppercase">
            Everyone&rsquo;s week
          </p>
          <span className="text-[10.5px] text-ink-3">
            {daytime ? 'all day' : 'after school'}
            {weekends ? null : <>&nbsp;&middot; weekdays</>}
          </span>
          <div className="flex flex-wrap gap-3 text-[11px] text-ink-2">
            <Key color={TEACHER_COLOR} label={`${teacherLabel} busy`} />
            <Key color={DANCER_COLOR} label="Dancer busy" />
          </div>
        </div>

        <AvailabilityGrid
          days={days}
          windowStart={drawnStart}
          windowEnd={data.windowEnd}
          picked={picked}
          lessonMinutes={minutes}
          onPick={(date, start) => onPick(date, start, addMinutes(start, minutes))}
        />
      </div>

      {/* Hers to edit (A-16). The one claim here a reader could act on wrongly. */}
      <p className="rounded-lg bg-surface-2 px-3 py-2.5 text-[12px] leading-relaxed text-ink-2">
        {data.disclosure}
      </p>
    </div>
  )
}

/**
 * An opt-in for a kind of time the list hides by default.
 *
 * Only rendered when there is something behind it, so the screen never offers
 * a switch that would reveal nothing — a control that does nothing is one that
 * gets reported as broken.
 */
function Opt({
  on,
  onClick,
  children,
}: {
  on: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`min-h-[32px] rounded-full border px-2.5 text-[11.5px] font-semibold ${
        on ? 'border-accent bg-accent-soft text-accent-2' : 'border-line-2 text-ink-3'
      }`}
    >
      {children}
    </button>
  )
}

function Key({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className="h-2 w-3.5 shrink-0 rounded-[2px]"
        style={{ background: color }}
      />
      {label}
    </span>
  )
}

function SuggestionButton({
  suggestion,
  on,
  onClick,
}: {
  suggestion: FreeBusySuggestion
  on: boolean
  onClick: () => void
}) {
  const [y, m, d] = suggestion.date.split('-').map(Number)
  const day = new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })

  return (
    <button
      type="button"
      onClick={onClick}
      /*
        Named explicitly, because the same time appears twice on this screen:
        once as a suggestion and once as the free block it came from. Read out
        as bare text the two are indistinguishable, and the shorter one is not
        necessarily the one focus lands on.
      */
      aria-label={`Suggested: ${day} at ${clockLabel(suggestion.start)}, ${fitLabel(suggestion.fit)}`}
      className={`min-h-[44px] min-w-0 rounded-xl border px-3 py-2 text-left ${
        on ? 'border-accent bg-accent text-on-accent' : 'border-line-2 bg-surface'
      }`}
    >
      <span className="block text-[12px] font-semibold opacity-75">{day}</span>
      <span className="tabular block text-[14px] font-bold">{clockLabel(suggestion.start)}</span>
      <span className="block text-[11px] opacity-70">{fitLabel(suggestion.fit)}</span>
    </button>
  )
}
